import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  Clock, 
  Star, 
  Users, 
  Package,
  Shield
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import toursHeroBg from "@/assets/tours-hero-bg.jpg";
import { useTourCategories } from "@/hooks/useTourCategories";
import { useTours } from "@/hooks/useTours";
import { useTourPackages } from "@/hooks/useTourPackages";

const PackageTours = () => {
  const { categories, loading: categoriesLoading } = useTourCategories();
  const { tours, loading: toursLoading } = useTours();
  
  const renderIcon = (iconName: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    return IconComponent ? <IconComponent className="h-8 w-8 text-white" /> : <Package className="h-8 w-8 text-white" />;
  };

  // Filter only package tours
  const packageTours = tours.filter(tour => tour.pricing_type === 'package');
  const featuredPackageTours = packageTours.filter(tour => tour.is_featured);

  // Filter categories that have package tours
  const categoriesWithPackages = categories.filter(category => 
    packageTours.some(tour => tour.category_id === category.id)
  );

  return (
    <Layout>
      {/* Hero Section */}
      <section 
        className="py-20 text-white relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${toursHeroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4 bg-white/20 text-white border-white/30">
              <Package className="h-4 w-4 mr-2" />
              Package Tours
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Discover Our Package Tours
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Experience the best of Dominican Republic with our carefully curated 
              package tours. Each package includes multiple options designed to fit 
              different group sizes and preferences at competitive prices.
            </p>
          </div>
        </div>
      </section>

      {/* Package Tour Categories */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Package Categories</h2>
            <p className="text-lg text-muted-foreground">Categories with package tour options</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {categoriesLoading ? (
              <div className="col-span-full text-center">Loading categories...</div>
            ) : categoriesWithPackages.length > 0 ? (
              categoriesWithPackages.map((category) => (
                <Card key={category.id} className="text-center hover:shadow-primary transition-all duration-300 hover:-translate-y-2 cursor-pointer border-0 shadow-md">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mb-4">
                      {renderIcon(category.icon)}
                    </div>
                    <CardTitle className="text-lg">{category.name}</CardTitle>
                    <CardDescription>
                      {packageTours.filter(tour => tour.category_id === category.id).length} package tours
                    </CardDescription>
                  </CardHeader>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center text-muted-foreground">
                No package categories available
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured Package Tours */}
      {featuredPackageTours.length > 0 && (
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-gradient-primary">Featured Package Tours</Badge>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                Most Popular Package Experiences
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Our top-rated package tours with flexible pricing options
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredPackageTours.map((tour) => (
                <Card key={tour.id} className="overflow-hidden hover:shadow-primary transition-all duration-300 hover:-translate-y-2 border-0 shadow-md">
                  <div 
                    className="aspect-video bg-cover bg-center bg-no-repeat relative"
                    style={{ backgroundImage: `url(${tour.main_image_url})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                    <Badge className="absolute top-4 left-4 bg-secondary text-secondary-foreground">
                      <Package className="h-3 w-3 mr-1" />
                      Featured Package
                    </Badge>
                  </div>
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <Badge variant="outline" className="mb-2 text-xs">
                          {tour.tour_categories?.name || 'Category'}
                        </Badge>
                        <Badge className="mb-2 ml-2 text-xs bg-gradient-primary">
                          Package Tour
                        </Badge>
                        <CardTitle className="text-xl">{tour.name}</CardTitle>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">${tour.base_price || 0}</div>
                        <div className="text-sm text-muted-foreground">starting from</div>
                      </div>
                    </div>
                    <CardDescription className="text-base">
                      {tour.short_description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">{tour.duration}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Max {tour.max_capacity}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium">4.9</span>
                        <span className="text-muted-foreground">(150+)</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {tour.features && tour.features.slice(0, 3).map((feature, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                      {tour.features && tour.features.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{tour.features.length - 3} more
                        </Badge>
                      )}
                    </div>
                    <Link to={`/tour/${tour.slug}`}>
                      <Button className="w-full bg-gradient-primary shadow-primary hover:shadow-secondary transition-all duration-300">
                        View Package Options
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Package Tours */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              All Package Tours
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Explore our complete collection of package tours with flexible pricing options
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {toursLoading ? (
              <div className="col-span-full text-center">Loading tours...</div>
            ) : packageTours.length > 0 ? (
              packageTours.map((tour) => (
                <Card key={tour.id} className="overflow-hidden hover:shadow-primary transition-all duration-300 hover:-translate-y-2 border-0 shadow-md">
                  <div 
                    className="aspect-video bg-cover bg-center bg-no-repeat relative"
                    style={{ backgroundImage: `url(${tour.main_image_url})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                    <Badge className="absolute top-4 left-4 bg-gradient-primary text-white">
                      <Package className="h-3 w-3 mr-1" />
                      Package
                    </Badge>
                  </div>
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <Badge variant="outline" className="mb-2 text-xs">
                          {tour.tour_categories?.name || 'Category'}
                        </Badge>
                        <Badge className="mb-2 ml-2 text-xs bg-gradient-primary">
                          Package Tour
                        </Badge>
                        <CardTitle className="text-xl">{tour.name}</CardTitle>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-primary">${tour.base_price || 0}</div>
                        <div className="text-sm text-muted-foreground">starting from</div>
                      </div>
                    </div>
                    <CardDescription className="text-base">
                      {tour.short_description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-4 text-sm">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">{tour.duration}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Multiple Options</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="font-medium">4.8</span>
                        <span className="text-muted-foreground">(100+)</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {tour.features && tour.features.slice(0, 2).map((feature, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                    <Link to={`/tour/${tour.slug}`}>
                      <Button className="w-full bg-gradient-primary shadow-primary hover:shadow-secondary transition-all duration-300">
                        View Package Details
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className="col-span-full text-center text-muted-foreground">
                No package tours available
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Booking Information */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-hero rounded-2xl p-8 md:p-12 text-white text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Why Choose Package Tours?
              </h2>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Our package tours offer flexible pricing options for different group sizes 
                and preferences. Choose the package that best fits your needs and enjoy 
                better value for money with our comprehensive tour experiences.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="flex items-center justify-center space-x-3">
                  <Package className="h-6 w-6" />
                  <span>Multiple Options</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <Users className="h-6 w-6" />
                  <span>Group Friendly</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <Star className="h-6 w-6" />
                  <span>Best Value</span>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 text-lg px-8 py-6">
                  Contact Us Today
                </Button>
                <Link to="/tours">
                  <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm text-lg px-8 py-6">
                    View All Tours
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PackageTours;